import { useModel, useNavigate, useRequest } from '@umijs/max';
import {
  Affix,
  Badge,
  Button,
  Drawer,
  Empty,
  Flex,
  List,
  notification,
  Popconfirm,
  Segmented,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { createStyles } from 'antd-style';
import cn from 'classnames';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { getNotificationList, updateNotificationAllRead } from '@/services/api';
import {
  EVENT_MODE_MAP,
  EVENT_TYPE_MAP,
  getEventList,
  updateEventData,
} from '@/services/event';
import { getResourceUrl } from '@/utils';
import EventAttachmentModal from '../EventAttachmentModal';
import IconFont from '../IconFont';

const { Paragraph, Text } = Typography;

const useStyles = createStyles(({ token }) => ({
  drawer: {
    width: 500,
    maxWidth: '100%',
  },
  header: {
    padding: 'var(--ant-padding-sm)',
    backgroundColor: 'white',
    boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)',
  },
  list: {
    padding: 'var(--ant-padding-sm)',
    flex: 1,
    overflow: 'auto',
    scrollbarWidth: 'thin',
    scrollbarGutter: 'stable',
  },
  item: {
    position: 'relative',
    paddingInline: 15,
    paddingBlock: 15,
    borderBottom: `0.5px solid #e5e5e5`,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    cursor: 'pointer',
    borderRadius: 8,
    '&:last-child': {
      borderBottom: 'none',
    },
    '&:hover': {
      background: 'linear-gradient(to right, #fcfcfc, #f5f5f5)',
    },
  },
  unread: {
    // paddingLeft: 35,
  },
  dot: {
    // position: 'absolute',
    // top: 23,
    // left: 20,
    display: 'inline-block',
    width: 6,
    height: 6,
    backgroundColor: token.red5,
    borderRadius: '50%',
    marginLeft: 2,
    marginRight: 12,
  },
  time: {
    fontSize: 12,
  },
  content: {
    marginBottom: '0 !important',
  },
  task: {
    fontSize: 12,
    marginBottom: '0 !important',
  },
}));

export default function NotificationCenter() {
  const navigate = useNavigate();
  const { styles } = useStyles();
  const [notify, notificationHolder] = notification.useNotification({
    top: 80,
  });
  const { notifications, addNotification, removeNotification } =
    useModel('notification');
  const ws = useModel('websocket');
  const { closeChatbot } = useModel('chatbot');
  const [open, setOpen] = useState(false);
  const [eventAttachmentModal, setEventAttachmentModal] = useState<{
    open: boolean;
    event?: API.EVENT.Event;
  }>({ open: false });
  const [activeTab, setActiveTab] = useState<API.EVENT.EventMode>(
    '1' as API.EVENT.EventMode,
  );

  const onOpen = () => setOpen(true);
  const onClose = () => setOpen(false);

  // 加载事件列表
  const {
    data: events = [],
    loading,
    run,
    refresh,
    mutate,
  } = useRequest(
    () =>
      getNotificationList({
        pageNum: 1,
        pageSize: 50, // 获取最近50条
        eventMode: activeTab,
      }),
    {
      manual: true,
      refreshDeps: [activeTab],
      formatResult: (res) => res.rows,
    },
  );

  // 阅读状态
  const { data: unreadItems = [], refresh: refreshUnreadItems } = useRequest(
    () =>
      getNotificationList({
        pageNum: 1,
        pageSize: 50,
      }),
    {
      formatResult: (res) =>
        res.rows
          .filter((item: API.EVENT.Event) => item.handledStatus === '0')
          .map((item) => item.eventId),
    },
  );

  const hasUnread = useMemo(() => unreadItems.length > 0, [unreadItems]);

  // 打开抽屉时刷新数据
  useEffect(() => {
    if (open) {
      run();
    }
  }, [open, activeTab]);

  const onViewAll = () => {
    if (activeTab === '1') {
      navigate('/events?mode=1');
    } else if (activeTab === '2') {
      navigate('/events?mode=2');
    } else if (activeTab === '3') {
      navigate('/reports');
    }
    onClose();
  };

  const onItemClick = (event: API.EVENT.Event) => {
    setEventRead(event);
    if ('reportId' in event && event.reportId) {
      navigate(`/reports/detail/${event.reportId}`);
    } else {
      navigate(`/events?mode=${event.eventMode}&id=${event.eventId}`);
    }
    onClose();
  };

  const onShowDetail = (event: API.EVENT.Event) => {
    setEventRead(event);
    closeChatbot();
    if ('reportId' in event && event.reportId) {
      navigate(`/reports/detail/${event.reportId}`);
    } else {
      setEventAttachmentModal({
        open: true,
        event,
      });
    }
    notify.destroy();
  };

  const setEventRead = async (event: API.EVENT.Event) => {
    if (event.handledStatus !== '0') return;
    const newData = {
      ...event,
      handledStatus: '3' as API.EVENT.HandledStatus,
    };
    await updateEventData(newData);
    mutate((prev) =>
      prev.map((item) => (item.eventId === event.eventId ? newData : item)),
    );
  };

  const onReadAll = async () => {
    await updateNotificationAllRead();
    refresh();
  };

  // 通知弹窗
  const openNotification = (msg: API.NOTIFICATION.Message) => {
    const event = msg.payload;
    const { color: typeColor, label: typeLabel } =
      EVENT_TYPE_MAP[event.eventType] || {};
    const { label: modeLabel } = EVENT_MODE_MAP[event.eventMode] || {};
    const isWorkReport = 'reportId' in event && !!event.reportId;
    notify.open({
      message: isWorkReport ? (
        <>
          <Tag bordered={false} color="blue">
            任务报告
          </Tag>
          <Text>
            {event.taskName} {event.alertContent}
          </Text>
        </>
      ) : (
        <>
          {modeLabel ? (
            <Tag bordered={false} color={typeColor}>
              {modeLabel}：{typeLabel}
            </Tag>
          ) : null}
          <Text>{event.alertContent}</Text>
        </>
      ),
      description: event.attachment ? (
        <img
          src={getResourceUrl(event.attachment)}
          alt="attachment"
          style={{ width: '100%', cursor: 'pointer' }}
          onClick={() => onShowDetail(event)}
        />
      ) : null,
      closable: false,
      duration: 0,
      onClose: () => {
        removeNotification(msg.key);
      },
      btn: (
        <Space>
          <Button
            color="default"
            variant="link"
            size="small"
            onClick={() => {
              removeNotification(msg.key);
              notify.destroy();
            }}
          >
            忽略
          </Button>
          {isWorkReport || event.attachment ? (
            <Button
              type="primary"
              size="small"
              onClick={() => onShowDetail(event)}
            >
              查看
            </Button>
          ) : (
            <Button
              color="primary"
              variant="link"
              size="small"
              onClick={() => {
                setEventRead({
                  ...event,
                  handledStatus: '0' as API.EVENT.HandledStatus,
                });
                removeNotification(msg.key);
                notify.destroy();
              }}
            >
              知道了
            </Button>
          )}
        </Space>
      ),
    });
  };

  useEffect(() => {
    if (notifications[0] && notifications[0].type === 'event') {
      openNotification(notifications[0]);
    }
  }, [notifications, removeNotification]);

  // 订阅 WebSocket 消息
  useEffect(() => {
    // 订阅消息
    if (ws) {
      ws.subscribe((msg: API.RobotMqttResponse) => {
        if (
          msg.type === '/work_alert' ||
          msg.type === '/device_alert' ||
          msg.type === '/work_report'
        ) {
          // 全局通知，传递数据给 NotificationCenter
          addNotification({
            type: 'event',
            payload: msg.payload,
          });
        }
      });
    }
  }, [ws]);

  return (
    <>
      {notificationHolder}
      <Button type="text" onClick={onOpen}>
        <Badge dot={hasUnread}>
          <IconFont type="icon-notification" style={{ fontSize: 23 }} />
        </Badge>
      </Button>
      <EventAttachmentModal
        {...eventAttachmentModal}
        onClose={() => setEventAttachmentModal({ open: false })}
      />
      <Drawer
        title="通知中心"
        closable={false}
        onClose={onClose}
        open={open}
        styles={{
          body: {
            padding: '0',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
        extra={[
          <Popconfirm
            key="clear"
            placement="bottom"
            title="确定要全部设为已读吗"
            okText="确定"
            cancelText="取消"
            onConfirm={onReadAll}
          >
            <Button type="link">全部已读</Button>
          </Popconfirm>,
        ]}
        footer={
          <Button block key="view-all" onClick={onViewAll} type="link">
            查看全部
          </Button>
        }
      >
        <div className={styles.header}>
          <Segmented
            value={activeTab}
            onChange={setActiveTab}
            options={Object.values(EVENT_MODE_MAP).map((item) => ({
              value: item.key as API.EVENT.EventMode,
              label: item.label,
            }))}
            block
          />
        </div>
        {events.length === 0 ? (
          <Empty description="暂无通知" style={{ marginTop: 50 }} />
        ) : (
          <div className={styles.list}>
            <Spin spinning={loading}>
              {events.map((event) => {
                const { color: typeColor, label: typeLabel } =
                  EVENT_TYPE_MAP[event.eventType] || {};
                const { label: modeLabel } =
                  EVENT_MODE_MAP[event.eventMode] || {};
                const isUnread = event.handledStatus === '0';
                // const isUnread = Math.random() > 0.5;
                const isWorkReport = 'reportId' in event && !!event.reportId;
                return (
                  <div
                    className={cn(styles.item, { [styles.unread]: isUnread })}
                    key={event.eventId}
                    onClick={() => onItemClick(event)}
                  >
                    {!isWorkReport && (
                      <Flex justify="space-between" align="center" gap={8}>
                        <Flex gap={0} align="center">
                          {isUnread && <span className={styles.dot}></span>}
                          <Tag
                            bordered={false}
                            color={typeColor}
                            style={{
                              padding: 0,
                              backgroundColor: 'transparent',
                              fontSize: 14,
                            }}
                          >
                            {typeLabel}
                          </Tag>
                        </Flex>
                        <Text type="secondary" className={styles.time}>
                          {dayjs(event.createTime).isValid()
                            ? dayjs(event.createTime).format('MM-DD HH:mm')
                            : '-'}
                        </Text>
                      </Flex>
                    )}

                    <Paragraph
                      className={styles.content}
                      ellipsis={{ rows: 2, expandable: true, symbol: '展开' }}
                    >
                      {isUnread && isWorkReport && (
                        <span className={styles.dot}></span>
                      )}
                      {event.alertContent}
                    </Paragraph>

                    {isWorkReport ? (
                      <Flex justify="space-between" align="center">
                        <Paragraph className={styles.task} type="secondary">
                          {event.taskName}
                        </Paragraph>
                        <Text type="secondary" className={styles.time}>
                          {dayjs(event.createTime).isValid()
                            ? dayjs(event.createTime).format('MM-DD HH:mm')
                            : '-'}
                        </Text>
                      </Flex>
                    ) : event.taskName ? (
                      <Paragraph className={styles.task} type="secondary">
                        {event.taskName}
                      </Paragraph>
                    ) : null}
                  </div>
                );
              })}
            </Spin>
          </div>
        )}
      </Drawer>
    </>
  );
}
